import { FunctionComponent, useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { BookingProcessStoreContext } from '../stores/BookingProcessStore';
import { useRootStoreContext } from '../stores/RootStore';
import LoadingSpinner from './common/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Timelapse } from '@material-ui/icons';
import { SuggestionItemType } from '../api/suggestion.api';
import { UIStoreNS } from '../stores/useUIStore';

const Style = styled.button<{
    isAvailable: boolean;
    isWaitingList: boolean;
}>`
    display: flex;
    height: 32px;
    padding: 8px;
    align-items: center;
    justify-content: center;
    font-style: normal;
    font-weight: bold;
    line-height: normal;
    font-size: 10px;
    letter-spacing: 1px;
    user-select: none;
    color: #fff;
    background: #ff6e3d;
    border-radius: 6px;
    border: 0px;
    position: relative;
    transition: all 0.1s ease-in-out;
    transform: scale(1);

    &:hover {
        cursor: pointer;
        background: #ff5116;
    }

    &:active {
        transform: scale(0.96543);
    }

    ${(props: { isAvailable: boolean }) =>
        !props.isAvailable &&
        css`
            background: #eeeeee !important; /* Important to override hover */
            color: #999;
        `}

    ${(props: { isAvailable: boolean; isWaitingList: boolean }) =>
        !props.isWaitingList &&
        !props.isAvailable &&
        css`
            text-decoration: line-through;
            cursor: not-allowed !important; /* Important to override hover */
        `}
`;

const MultipleBookingOptionsAsterisk = styled.i`
    position: absolute;
    top: 2px;
    right: 5px;
    color: #fcde7e;
    font-size: 14px;
    display: block;
    user-select: none;
    pointer-events: none;
    font-style: normal;
`;

const StyleLoading = styled.button`
    display: flex;
    height: 32px;
    padding: 8px;
    align-items: center;
    justify-content: center;

    font-style: normal;
    font-weight: bold;
    line-height: normal;
    font-size: 2px;
    letter-spacing: 1px;
    user-select: none;

    color: #fff;
    background: #eeeeee;
    border-radius: var(--radius);
    border: 0px;
`;

type Props = {
    data?: SuggestionItemType;
    isLoading?: boolean;
    restaurantId?: number;
    restaurantSlug?: string;
    guests?: number;
    eventId?: number;
    eventSlug?: string;
    isWaitingList?: boolean; // Override waiting list
    isWaitingListWhenFullyBooked?: boolean; // Fully booked auto waiting list
    showUnavailableTimes?: boolean;
};

export const SuggestionItem: FunctionComponent<Props> = ({
    data = {
        date: undefined,
        isAvailable: false,
        isTableAvailable: false,
        isEventAvailable: false,
        isWaitingListAvailable: false,
    },
    isLoading = false,
    restaurantId,
    restaurantSlug,
    guests,
    eventId,
    isWaitingList,
    isWaitingListWhenFullyBooked,
}) => {
    const BookingProcessStore = useContext(BookingProcessStoreContext);
    const [bookingProcessStarting, setBookingProcessStarting] = useState(false);
    const {
        UIStore: {
            reducer: [, dispatch],
            closeModal,
        },
    } = useRootStoreContext();

    const { t } = useTranslation();

    // TODO Re-enable this at some point when the reserve page has more details
    // const startEventBookingProcess = (event: EventRaw) => {
    //   setBookingProcessStarting(true);
    //   BookingProcessStore.startBookingProcess(
    //     event.restaurantId,
    //     moment(data.date, moment.defaultFormatUtc).format('YYYY-MM-DDTHH:mm'),
    //     guests!,
    //     event.id,
    //     false,
    //   ).then(() => {
    //     setBookingProcessStarting(false);
    //     closeModal();
    //   });
    // };

    const startBookingProcess = (isBookingForWaitingList: boolean) => {
        setBookingProcessStarting(true);
        BookingProcessStore.startBookingProcess(
            restaurantId!,
            restaurantSlug!,
            moment(data.date, moment.defaultFormatUtc).format(
                'YYYY-MM-DDTHH:mm',
            ),
            guests!,
            eventId,
            isBookingForWaitingList,
            isWaitingListWhenFullyBooked,
        ).then(() => {
            setBookingProcessStarting(false);
            closeModal();
        });
    };

    // TODO rewrite this scheit kv adam
    const onClick = (reserveTable?: boolean, openDirectly?: boolean) => {
        if (!data.isWaitingListAvailable && !data.isAvailable) {
            alert(t('components.suggestionItem.fullMessage'));
            return;
        }

        // Should never happen but good 4 debugging hehehe
        if (!data.date) {
            alert('Invalid date');
            return;
        }

        const isBookingForWaitingList =
            isWaitingList || data.isWaitingListAvailable;

        // We have both a table and an event - user has to make a choice
        // If we're coming from the Event page then we have the eventId and we should not check for table availability.
        if (
            !reserveTable &&
            data.isEventAvailable &&
            !(eventId && openDirectly)
        ) {
            // This opens a modal (which contains a secret portal to narnia)
            dispatch({
                type: UIStoreNS.ActionType.ModalConfig,
                payload: {
                    type: 'BookingSelection',
                    title: 'Select your type of booking',
                    restaurantId: restaurantId,
                    restaurantSlug: restaurantSlug,
                    date: data.date.toDate(),
                    isTableAvailable: data.isTableAvailable,
                    callback: (action, payload) => {
                        // Close the modal and lfg.
                        closeModal();

                        if (action === 'event') {
                            if (!payload) {
                                throw new Error(
                                    'Invalid payload for event selection',
                                );
                            }

                            // TODO Re-enable this at some point when the reserve page has more details
                            // about which event is being booked
                            // startEventBookingProcess(payload as EventRaw);]

                            if (restaurantSlug) {
                                window.location.href = `https://www.dineout.is/${restaurantSlug}/event/${
                                    payload.slug
                                }?step=1&date=${data.date?.toString()}&guests=${guests}`;
                            } else {
                                alert(
                                    'An error came up while trying to start the booking process for this event',
                                );
                            }
                        } else {
                            // Recursive madness
                            onClick(true);
                        }
                    },
                    onClose: () => {
                        closeModal();
                    },
                } as UIStoreNS.BookingSelectionPayload,
            });

            return;
        }

        startBookingProcess(isBookingForWaitingList);
    };

    return isLoading || bookingProcessStarting ? (
        <StyleLoading>
            <LoadingSpinner />
        </StyleLoading>
    ) : (
        <Style
            isAvailable={data.isAvailable}
            isWaitingList={data.isWaitingListAvailable}
            onClick={() =>
                onClick(undefined, eventId !== undefined ? true : undefined)
            }
        >
            {data.isEventAvailable && eventId === undefined && (
                <MultipleBookingOptionsAsterisk>
                    *
                </MultipleBookingOptionsAsterisk>
            )}
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {data.isWaitingListAvailable && (
                    <Timelapse
                        color="primary"
                        fontSize="small"
                        style={{ marginRight: 4 }}
                    />
                )}
                <div>
                    {data.date &&
                        moment(data.date, moment.defaultFormatUtc).format(
                            'HH:mm',
                        )}
                </div>
            </div>
        </Style>
    );
};
